import {
  useGetMyPositionsQuery,
  useGetMyHourlyMetricsQuery,
  useGetMyCyclesQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { PnLPill } from "@/components/domain/PnLPill";
import { StatusPill } from "@/components/domain/StatusPill";
import { fmtUsd, fmtTime, fmtNum } from "@/lib/format";
import { EquityCurve } from "@/components/charts/EquityCurve";
import { DailyPnL } from "@/components/charts/DailyPnL";
import { CycleHeatmap } from "@/components/charts/CycleHeatmap";

export default function UserPerformance() {
  const closed = useGetMyPositionsQuery(
    { status: "closed", limit: 200 },
    { pollingInterval: 60000 },
  );
  const metrics = useGetMyHourlyMetricsQuery(20, { pollingInterval: 60000 });
  const cycles = useGetMyCyclesQuery(200);

  const closedRows: any[] = closed.data ?? [];
  const hm = metrics.data as any;

  // CycleHeatmap reads `started_at` from each row; user_cycles surfaces
  // the parent's start as `cycle_started_at`. Adapt the shape.
  const heatmapCycles = (cycles.data ?? []).map((r) => ({
    id: r.id,
    started_at: r.cycle_started_at,
    status: r.status,
  })) as any[];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Your equity curve — closed hourly positions</CardTitle>
        </CardHeader>
        <CardContent>
          {closed.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <EquityCurve positions={closedRows as any} height={260} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Your cycle activity heatmap
            <span className="text-xs font-normal text-muted ml-2">UTC times</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cycles.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <CycleHeatmap cycles={heatmapCycles as any} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Your rolling stats
            <span className="text-xs font-normal text-muted ml-2">
              window of {hm?.window ?? 0} closed positions
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {metrics.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : hm?.insufficient_data ? (
            <p className="text-sm text-muted">
              Need at least 5 closed positions for metrics — currently {hm.window ?? 0}.
            </p>
          ) : (
            <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
              <Stat label="Win rate"
                value={hm?.win_rate != null ? `${(hm.win_rate * 100).toFixed(1)}%` : "-"} />
              <Stat label="Sharpe (proxy)" value={String(hm?.sharpe ?? "-")} />
              <Stat label="Max drawdown" value={fmtUsd(hm?.max_drawdown ?? null)} />
              <Stat label="Consecutive losses" value={String(hm?.consecutive_losses ?? 0)} />
              <StatPnL label="Expectancy / trade" value={hm?.expectancy ?? null} />
              <StatPnL label="Avg win" value={hm?.avg_win ?? null} />
              <StatPnL label="Avg loss" value={hm?.avg_loss ?? null} />
              <StatPnL label="Total P&L" value={hm?.total_pnl ?? null} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your daily P&L — last 14 days</CardTitle>
        </CardHeader>
        <CardContent>
          {closed.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <DailyPnL positions={closedRows as any} days={14} height={220} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Your recent closed positions
            <span className="text-xs font-normal text-muted ml-2">
              {closedRows.length} rows
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {closed.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : closedRows.length === 0 ? (
            <p className="text-sm text-muted">No closed positions yet.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>id</TH>
                  <TH>Symbol</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Entry</TH>
                  <TH className="text-right">Exit</TH>
                  <TH className="text-right">Net P&L</TH>
                  <TH className="text-right">Fees</TH>
                  <TH>Closed</TH>
                </TR>
              </THead>
              <TBody>
                {closedRows.map((p) => (
                  <TR key={p.id}>
                    <TD>{p.id}</TD>
                    <TD>{p.symbol}</TD>
                    <TD><StatusPill status={p.status} /></TD>
                    <TD className="text-right tabular">
                      {p.entry_price != null ? fmtNum(+p.entry_price, 6) : "-"}
                    </TD>
                    <TD className="text-right tabular">
                      {p.exit_price != null ? fmtNum(+p.exit_price, 6) : "-"}
                    </TD>
                    <TD className="text-right">
                      <PnLPill value={p.net_pnl_usd ?? null} />
                    </TD>
                    <TD className="text-right tabular">{fmtUsd(p.fees_paid ?? null)}</TD>
                    <TD className="text-xs text-muted">{fmtTime(p.exit_time ?? null)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted uppercase tracking-wide mb-1">{label}</div>
      <div className="tabular text-lg font-semibold">{value}</div>
    </div>
  );
}

function StatPnL({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted uppercase tracking-wide mb-1">{label}</div>
      <div className="text-lg font-semibold">
        <PnLPill value={value} />
      </div>
    </div>
  );
}
